import { TemperatureUnits } from "@/libs/units/temperature";

// N48: =(((C7/1000000)/(58.4428))/1000)
export function calculateMolarSaltConcentration(salinity_ppm: number): number {
  return salinity_ppm / 1000000 / 58.4428 / 1000;
}

export function calculateSaltConcentration(salinity_ppm: number): number {
  return salinity_ppm / 1000000;
}

// C74: =(R52*(C72/100)^2+R53*(C72/100)+R54)/(R55*(C72/100)^2+R56*(C72/100)+1)
export function calculatePwAt70Mpa(
  R52: number,
  R53: number,
  R54: number,
  R55: number,
  R56: number,
  stockTankTemperatureTo: number
): number {
  return (
    (R52 * Math.pow(stockTankTemperatureTo / 100, 2) +
      R53 * (stockTankTemperatureTo / 100) +
      R54) /
    (R55 * Math.pow(stockTankTemperatureTo / 100, 2) +
      R56 * (stockTankTemperatureTo / 100) +
      1)
  );
}

// C76: =(S52*(C72/100)^2+S53*(C72/100)+S54)/(S55*(C72/100)^2+S56*(C72/100)+1)
export function calculateEwo(
  S52: number,
  S53: number,
  S54: number,
  S55: number,
  S56: number,
  stockTankTemperatureTo: number
): number {
  return (
    (S52 * Math.pow(stockTankTemperatureTo / 100, 2) +
      S53 * (stockTankTemperatureTo / 100) +
      S54) /
    (S55 * Math.pow(stockTankTemperatureTo / 100, 2) +
      S56 * (stockTankTemperatureTo / 100) +
      1)
  );
}

// C78: =(T52*(C72/100)^2+T53*(C72/100)+T54)/(T55*(C72/100)^2+T56*(C72/100)+1)
export function calculateFwo(
  T52: number,
  T53: number,
  T54: number,
  T55: number,
  T56: number,
  stockTankTemperatureTo: number
): number {
  return (
    (T52 * Math.pow(stockTankTemperatureTo / 100, 2) +
      T53 * (stockTankTemperatureTo / 100) +
      T54) /
    (T55 * Math.pow(stockTankTemperatureTo / 100, 2) +
      T56 * (stockTankTemperatureTo / 100) +
      1)
  );
}

// C80: =(1/70)*(1/(C76*(C70/70)+C78))
export function calculatePureWaterCompressibility(
  stockTankPressurePo: number,
  Ewo: number,
  Fwo: number
): number {
  return (1 / 70) * (1 / (Ewo * (stockTankPressurePo / 70) + Fwo));
}

// C82: =(1/C76)*LN(C76+C78)
export function calculatelw0At70Mpa(Ewo: number, Fwo: number): number {
  return (1 / Ewo) * Math.log(Ewo + Fwo);
}

// C84: =(1/C76)*LN(C76*(C70/70)+C78)
export function calculatelwAtStockTank(
  stockTankPressurePo: number,
  Ewo: number,
  Fwo: number
): number {
  return (1 / Ewo) * Math.log(Ewo * (stockTankPressurePo / 70) + Fwo);
}

// C86: =C74*EXP(C84-C82)
export function calculatePureWaterDensity(
  pw70Mpa: number,
  lwStockTank: number,
  lw70Mpa: number
): number {
  return pw70Mpa * Math.exp(lwStockTank - lw70Mpa);
}

// C88: =(R66*(C72/100)^2+R67*(C72/100)+R68)/(R69*(C72/100)^2+R70*(C72/100)+1)
export function calculateDm2(
  R66: number,
  R67: number,
  R68: number,
  R69: number,
  R70: number,
  stockTankTemperatureTo: number
): number {
  return (
    (R66 * Math.pow(stockTankTemperatureTo / 100, 2) +
      R67 * (stockTankTemperatureTo / 100) +
      R68) /
    (R69 * Math.pow(stockTankTemperatureTo / 100, 2) +
      R70 * (stockTankTemperatureTo / 100) +
      1)
  );
}

// C90: =(S66*(C72/100)^2+S67*(C72/100)+S68)/(S69*(C72/100)^2+S70*(C72/100)+1)
export function calculateDm32(
  S66: number,
  S67: number,
  S68: number,
  S69: number,
  S70: number,
  stockTankTemperatureTo: number
): number {
  return (
    (S66 * Math.pow(stockTankTemperatureTo / 100, 2) +
      S67 * (stockTankTemperatureTo / 100) +
      S68) /
    (S69 * Math.pow(stockTankTemperatureTo / 100, 2) +
      S70 * (stockTankTemperatureTo / 100) +
      1)
  );
}

// C92: =(T66*(C72/100)^2+T67*(C72/100)+T68)/(T69*(C72/100)^2+T70*(C72/100)+1)
export function calculateDm1(
  T66: number,
  T67: number,
  T68: number,
  T69: number,
  T70: number,
  stockTankTemperatureTo: number
): number {
  return (
    (T66 * Math.pow(stockTankTemperatureTo / 100, 2) +
      T67 * (stockTankTemperatureTo / 100) +
      T68) /
    (T69 * Math.pow(stockTankTemperatureTo / 100, 2) +
      T70 * (stockTankTemperatureTo / 100) +
      1)
  );
}

// C94: =(U66*(C72/100)^2+U67*(C72/100)+U68)/(U69*(C72/100)^2+U70*(C72/100)+1)
export function calculateDm12(
  U66: number,
  U67: number,
  U68: number,
  U69: number,
  U70: number,
  stockTankTemperatureTo: number
): number {
  return (
    (U66 * Math.pow(stockTankTemperatureTo / 100, 2) +
      U67 * (stockTankTemperatureTo / 100) +
      U68) /
    (U69 * Math.pow(stockTankTemperatureTo / 100, 2) +
      U70 * (stockTankTemperatureTo / 100) +
      1)
  );
}

// C96: =C74+C88*N48^2+C90*N48^(3/2)+C92*N48+C94*N48^(1/2)
export function calculatePbwAt70Mpa(
  pw70Mpa: number,
  Dm2: number,
  Dm32: number,
  Dm1: number,
  Dm12: number,
  molarSaltConcentration: number
): number {
  return (
    pw70Mpa +
    Dm2 * Math.pow(molarSaltConcentration, 2) +
    Dm32 * Math.pow(molarSaltConcentration, 3 / 2) +
    Dm1 * molarSaltConcentration +
    Dm12 * Math.sqrt(molarSaltConcentration)
  );
}

// C98: =(R77*(C72/100)^2+R78*(C72/100)+R79)/(R80*(C72/100)^2+R81*(C72/100)+1)
export function calculateEm(
  R77: number,
  R78: number,
  R79: number,
  R80: number,
  R81: number,
  stockTankTemperatureTo: number
): number {
  return (
    (R77 * Math.pow(stockTankTemperatureTo / 100, 2) +
      R78 * (stockTankTemperatureTo / 100) +
      R79) /
    (R80 * Math.pow(stockTankTemperatureTo / 100, 2) +
      R81 * (stockTankTemperatureTo / 100) +
      1)
  );
}

// C100: =(S77*(C72/100)^2+S78*(C72/100)+S79)/(S80*(C72/100)^2+S81*(C72/100)+1)
export function calculateFm32(
  S77: number,
  S78: number,
  S79: number,
  S80: number,
  S81: number,
  stockTankTemperatureTo: number
): number {
  return (
    (S77 * Math.pow(stockTankTemperatureTo / 100, 2) +
      S78 * (stockTankTemperatureTo / 100) +
      S79) /
    (S80 * Math.pow(stockTankTemperatureTo / 100, 2) +
      S81 * (stockTankTemperatureTo / 100) +
      1)
  );
}

// C102: =(T77*(C72/100)^2+T78*(C72/100)+T79)/(T80*(C72/100)^2+T81*(C72/100)+1)
export function calculateFm1(
  T77: number,
  T78: number,
  T79: number,
  T80: number,
  T81: number,
  stockTankTemperatureTo: number
): number {
  return (
    (T77 * Math.pow(stockTankTemperatureTo / 100, 2) +
      T78 * (stockTankTemperatureTo / 100) +
      T79) /
    (T80 * Math.pow(stockTankTemperatureTo / 100, 2) +
      T81 * (stockTankTemperatureTo / 100) +
      1)
  );
}

// C104: =(U77*(C72/100)^2+U78*(C72/100)+U79)/(U80*(C72/100)^2+U81*(C72/100)+1)
export function calculateFm12(
  U77: number,
  U78: number,
  U79: number,
  U80: number,
  U81: number,
  stockTankTemperatureTo: number
): number {
  return (
    (U77 * Math.pow(stockTankTemperatureTo / 100, 2) +
      U78 * (stockTankTemperatureTo / 100) +
      U79) /
    (U80 * Math.pow(stockTankTemperatureTo / 100, 2) +
      U81 * (stockTankTemperatureTo / 100) +
      1)
  );
}

// C106: =C76+C98*N48
export function calculateEb(
  Ewo: number,
  Em: number,
  molarSaltConcentration: number
): number {
  return Ewo + Em * molarSaltConcentration;
}

// C108: =C78+C100*N48^(3/2)+C102*N48+C104*N48^(1/2)
export function calculateFb(
  Fwo: number,
  Fm32: number,
  Fm1: number,
  Fm12: number,
  molarSaltConcentration: number
): number {
  return (
    Fwo +
    Fm32 * Math.pow(molarSaltConcentration, 3 / 2) +
    Fm1 * molarSaltConcentration +
    Fm12 * Math.sqrt(molarSaltConcentration)
  );
}

// C110: =(1/70)*(1/(C106*(C70/70)+C108))
export function calculateFbCompressibility(
  stockTankPressurePo: number,
  Eb: number,
  Fb: number
): number {
  return (1 / 70) * (1 / (Eb * (stockTankPressurePo / 70) + Fb));
}

// C112: =(1/C106)*LN(C106+C108)
export function calculatelbAt70Mpa(Eb: number, Fb: number): number {
  return (1 / Eb) * Math.log(Eb + Fb);
}

// C114: =(1/C106)*LN(C106*(C70/70)+C108)
export function calculatelbAtStockTank(
  stockTankPressurePo: number,
  Eb: number,
  Fb: number
): number {
  return (1 / Eb) * Math.log(Eb * (stockTankPressurePo / 70) + Fb);
}

// C116: =C96*EXP(C114-C112)
export function calculateBrineWaterDensity(
  PbwAt70Mpa: number,
  lbAtStockTank: number,
  lbAt70Mpa: number
): number {
  return PbwAt70Mpa * Math.exp(lbAtStockTank - lbAt70Mpa);
}

// C118: =1/C116
export function calculateVbo(brineWaterDensity: number): number {
  return 1 / brineWaterDensity;
}

// C120: =C68/C118
export function calculateWaterVolumeFactor(
  volumeAtReservoir: number,
  Vbo: number
): number {
  return volumeAtReservoir / Vbo;
}

const stockTankTemperatureC = TemperatureUnits.Celsius.toThisUnit(60);

// Coefficients for pure water density
export const Pwd = [-0.127213, 0.645486, 1.03265, -0.070291, 0.639589];
export const Ewd = [4.221, -3.478, 6.221, 0.5182, -0.4405];
export const Fwd = [-11.403, 29.932, 27.952, 0.20684, 0.3768];

// Coefficients for brine density
export const Dm2Coeff = [-0.00011149, 0.00017105, -0.00043766, 0, 0];
export const Dm32Coeff = [-0.0008878, -0.0001388, -0.00296318, 0, 0.51103];
export const Dm1Coeff = [0.0021466, 0.012427, 0.042648, -0.081009, 0.525417];
export const Dm12Coeff = [0.0002356, -0.0003636, -0.0002278, 0, 0];

// Coefficients for brine compressibility
export const EmCoeff = [0, 0, 0.1249, 0, 0];
export const Fm32Coeff = [-0.617, -0.747, -0.4339, 0, 10.26];
export const Fm1Coeff = [0, 9.917, 5.1128, 0, 3.892];
export const Fm12Coeff = [0.0365, -0.0369, 0, 0, 0];

export const pwAt70Mpa = calculatePwAt70Mpa(
  Pwd[0],
  Pwd[1],
  Pwd[2],
  Pwd[3],
  Pwd[4],
  stockTankTemperatureC
);

export const EwoVar = calculateEwo(
  Ewd[0],
  Ewd[1],
  Ewd[2],
  Ewd[3],
  Ewd[4],
  stockTankTemperatureC
);

export const FwoVar = calculateFwo(
  Fwd[0],
  Fwd[1],
  Fwd[2],
  Fwd[3],
  Fwd[4],
  stockTankTemperatureC
);

export const Dm2Var = calculateDm2(
  Dm2Coeff[0],
  Dm2Coeff[1],
  Dm2Coeff[2],
  Dm2Coeff[3],
  Dm2Coeff[4],
  stockTankTemperatureC
);

export const Dm32Var = calculateDm32(
  Dm32Coeff[0],
  Dm32Coeff[1],
  Dm32Coeff[2],
  Dm32Coeff[3],
  Dm32Coeff[4],
  stockTankTemperatureC
);

export const Dm1Var = calculateDm1(
  Dm1Coeff[0],
  Dm1Coeff[1],
  Dm1Coeff[2],
  Dm1Coeff[3],
  Dm1Coeff[4],
  stockTankTemperatureC
);

export const Dm12Var = calculateDm12(
  Dm12Coeff[0],
  Dm12Coeff[1],
  Dm12Coeff[2],
  Dm12Coeff[3],
  Dm12Coeff[4],
  stockTankTemperatureC
);

export const EmVar = calculateEm(
  EmCoeff[0],
  EmCoeff[1],
  EmCoeff[2],
  EmCoeff[3],
  EmCoeff[4],
  stockTankTemperatureC
);

export const Fm32Var = calculateFm32(
  Fm32Coeff[0],
  Fm32Coeff[1],
  Fm32Coeff[2],
  Fm32Coeff[3],
  Fm32Coeff[4],
  stockTankTemperatureC
);

export const Fm1Var = calculateFm1(
  Fm1Coeff[0],
  Fm1Coeff[1],
  Fm1Coeff[2],
  Fm1Coeff[3],
  Fm1Coeff[4],
  stockTankTemperatureC
);

export const Fm12Var = calculateFm12(
  Fm12Coeff[0],
  Fm12Coeff[1],
  Fm12Coeff[2],
  Fm12Coeff[3],
  Fm12Coeff[4],
  stockTankTemperatureC
);
